import { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import styles from './VisitorCounter.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import LoaderButton from '../../Molecules/Loader/LoaderButton';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import {
  useFetchVisitorStatistics,
  useFetchVisitorSettings,
  useUpdateVisitorSettings,
} from '../../../Hooks/VisitorCounter';

/**
 * عدد الزوار — the visitor count, and the switch that governs whether it is
 * shown in the public website's footer.
 *
 * Requirements V1, V3 and V5 of the brief. Plan:
 * DPA-FE `docs/Announcements&visitors/01-development-roadmap.md`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * WHY THIS SCREEN SHOWS FIVE NUMBERS AND NOT ONE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The requirement says "display the website visitor count clearly within the
 * CMS". An administrator shown a single large number asks "since when?" within
 * about two seconds, and there is no way to answer that from one figure. Total,
 * unique, today, 7 days and 30 days is the smallest set that answers it — and
 * `firstRecordedOn` is printed under the total so the period is stated rather
 * than implied.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE SENTENCE BESIDE THE TOGGLE IS LOAD-BEARING
 * ────────────────────────────────────────────────────────────────────────────
 *
 * «إخفاء العدد لا يوقف احتسابه». Turning the switch off removes the figure from
 * the website footer and nothing else: counting continues, and this screen
 * keeps showing the real number. An operator who reads "off" as "stop counting"
 * will later see a figure that grew while it was supposedly stopped and report
 * it as a bug. Requirement V5 is explicit on this point, so the screen is too.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * NO POLLING, AND NO "LIVE" FRAMING
 * ────────────────────────────────────────────────────────────────────────────
 *
 * This is a count, not a dashboard. A number that ticks upward while someone is
 * reading it is harder to write down than one that holds still, and the public
 * endpoint is cached for a minute in any case. `asOf` says when the figure was
 * taken, and there is an explicit refresh.
 */

/** Locale-grouped digits. The API sends a real number — never pre-formatted. */
const formatNumber = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('ar-AE').format(value);
  } catch {
    return String(value);
  }
};

const formatInstant = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('ar-AE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const formatDay = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('ar-AE', { day: '2-digit', month: 'short' });
};

/* -------------------------------------------------------------------------- */
/*  Date range                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * `<input type="date">` gives a bare `YYYY-MM-DD` with no time and no zone.
 * The API wants ISO-8601 UTC. These two do the conversion, and the asymmetry
 * between them is the whole point:
 *
 *   from -> 00:00:00.000 LOCAL on that day
 *   to   -> 23:59:59.999 LOCAL on that day
 *
 * Sending `to` as midnight would exclude the entire last day the operator
 * picked — they would ask for «1 to 7 October» and be given six days. That is
 * the classic off-by-one in every date-range filter, and it is silent: the
 * number is simply lower than it should be.
 */
const dayStartToUtc = (value) => {
  if (!value) return '';
  const local = new Date(`${value}T00:00:00`);
  return Number.isNaN(local.getTime()) ? '' : local.toISOString();
};

const dayEndToUtc = (value) => {
  if (!value) return '';
  const local = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(local.getTime()) ? '' : local.toISOString();
};

/** `YYYY-MM-DD` for an `<input type="date">`, in the operator's own zone. */
const toDateInput = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
};

const startOfThisMonth = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInput(date);
};

/** The shortcuts an operator actually asks for, rather than a blank pair. */
const RANGE_PRESETS = [
  { key: '7d', label: 'آخر 7 أيام', from: () => daysAgo(6), to: () => toDateInput(new Date()) },
  { key: '30d', label: 'آخر 30 يومًا', from: () => daysAgo(29), to: () => toDateInput(new Date()) },
  { key: 'month', label: 'هذا الشهر', from: startOfThisMonth, to: () => toDateInput(new Date()) },
  { key: '12m', label: 'آخر 12 شهرًا', from: () => daysAgo(364), to: () => toDateInput(new Date()) },
];

const VisitorCounterPage = () => {
  const customizedToast = useToast();

  /**
   * `draft` is what the operator is typing; `range` is what has been APPLIED.
   *
   * Two separate pieces of state on purpose. Bound directly to the query, a
   * half-typed year (`0202-10-07`) would fire a request on every keystroke and
   * redraw the chart with nonsense in between. Nothing is fetched until
   * «تطبيق».
   */
  const [draft, setDraft] = useState({ from: '', to: '' });
  const [range, setRange] = useState({ from: '', to: '' });

  const {
    data: stats,
    isLoading: isLoadingStats,
    isError: isStatsError,
    error: statsError,
    refetch: refetchStats,
    isFetching: isRefetching,
  } = useFetchVisitorStatistics({
    from: dayStartToUtc(range.from),
    to: dayEndToUtc(range.to),
  });

  const hasRange = Boolean(range.from || range.to);

  /* Checked on the draft, so the operator sees it before pressing «تطبيق». */
  const rangeError =
    draft.from && draft.to && draft.from > draft.to
      ? 'تاريخ البداية يجب أن يكون قبل تاريخ النهاية'
      : '';

  const applyPreset = (preset) => {
    const next = { from: preset.from(), to: preset.to() };
    setDraft(next);
    setRange(next);
  };

  const clearRange = () => {
    setDraft({ from: '', to: '' });
    setRange({ from: '', to: '' });
  };

  const {
    data: settings,
    isLoading: isLoadingSettings,
    isError: isSettingsError,
    refetch: refetchSettings,
  } = useFetchVisitorSettings();

  const { mutate: updateSettings, isLoading: isSaving } =
    useUpdateVisitorSettings();

  /* A confirmation step on the way OFF only. Turning the count on is harmless
   * and reversible; turning it off changes what every visitor sees on every
   * page of the public site, and an accidental click on a bare switch is how
   * that happens without anyone noticing for a week. */
  const [pendingDisable, setPendingDisable] = useState(false);

  const isEnabled = settings?.isFooterDisplayEnabled === true;

  const applyToggle = (next) => {
    updateSettings(next, {
      onSuccess: () => {
        setPendingDisable(false);
        /* Composed here, in Arabic, from the state the server acknowledged —
           the envelope's `message` does not survive `apiClient`'s unwrap and
           arrives in English in any case. See Hooks/VisitorCounter.js. */
        customizedToast.open(
          next
            ? 'تم إظهار عدد الزوار في تذييل الموقع'
            : 'تم إخفاء عدد الزوار من تذييل الموقع'
        );
        refetchSettings();
      },
      onError: (err) => {
        setPendingDisable(false);
        // A 403 here means "known but not allowed" and carries its own sentence.
        toast.error(err?.message ?? 'تعذّر حفظ الإعداد');
      },
    });
  };

  const chart = useMemo(() => {
    const daily = stats?.daily ?? [];
    return {
      series: [
        { name: 'زيارات', data: daily.map((point) => point?.visits ?? 0) },
        {
          name: 'زوار فريدون',
          data: daily.map((point) => point?.uniqueVisitors ?? 0),
        },
      ],
      options: {
        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'inherit' },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['rgba(13, 160, 110, 1)', 'rgba(37, 99, 235, 1)'],
        fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0 } },
        xaxis: { categories: daily.map((point) => formatDay(point?.date)) },
        legend: { position: 'top', horizontalAlign: 'right' },
        grid: { borderColor: 'rgba(0,0,0,0.06)' },
        tooltip: { shared: true },
      },
    };
  }, [stats]);

  /**
   * What the chosen range adds up to, derived from `daily` — which the contract
   * says is capped to the requested range.
   *
   * ⚠ VISITS ARE SUMMED. UNIQUE VISITORS ARE NOT, AND MUST NOT BE.
   *
   * Someone who came on Monday and again on Tuesday is counted in BOTH days'
   * `uniqueVisitors`, but is ONE unique visitor over the period. Adding the
   * daily figures would therefore overstate it — by more the longer the range,
   * and most on exactly the engaged audience the Academy cares about. Only the
   * server can de-duplicate across days, because only the server holds the
   * identifiers. So this reports a visit total and the best day, and says
   * nothing about uniques it cannot honestly compute.
   */
  const rangeSummary = useMemo(() => {
    const daily = stats?.daily ?? [];
    if (!hasRange || daily.length === 0) return null;

    const visits = daily.reduce((sum, point) => sum + (point?.visits ?? 0), 0);
    const best = daily.reduce(
      (top, point) => ((point?.visits ?? 0) > (top?.visits ?? -1) ? point : top),
      null
    );

    return {
      visits,
      days: daily.length,
      average: Math.round(visits / daily.length),
      best,
    };
  }, [stats, hasRange]);

  const tiles = [
    { key: 'totalVisits', label: 'إجمالي الزيارات', value: stats?.totalVisits },
    { key: 'uniqueVisitors', label: 'زوار فريدون', value: stats?.uniqueVisitors },
    { key: 'visitsToday', label: 'زيارات اليوم', value: stats?.visitsToday },
    { key: 'visitsLast7Days', label: 'آخر 7 أيام', value: stats?.visitsLast7Days },
    { key: 'visitsLast30Days', label: 'آخر 30 يومًا', value: stats?.visitsLast30Days },
  ];

  return (
    <div className={styles.page}>
      {/* ═══════════════════════════════════════════════════ toolbar ═══════
          Title, live meta and EVERY control in one band at the top.

          The filters used to sit at the BOTTOM of the statistics card, below
          the five tiles — so the control that changes the chart was underneath
          the chart's own numbers, and an operator had to scroll past the answer
          to reach the question. Global controls belong above the data they
          govern, on the first screenful.

          One row on a desktop CMS, wrapping to two or three as the viewport
          narrows, which is why nothing here carries a fixed width. */}
      <header className={styles.toolbar}>
        <div className={styles.toolbarMain}>
          <h2 className={styles.title}>عدد الزوار</h2>
          {/* The "since when" meta, inline rather than its own paragraph — it
              is a caption for the numbers, not a statement in its own right. */}
          {!isLoadingStats && !isStatsError && (
            <p className={styles.meta}>
              منذ {formatInstant(stats?.firstRecordedOn)} · حتى{' '}
              {formatInstant(stats?.asOf)}
            </p>
          )}
        </div>

        <div className={styles.toolbarControls}>
          <div className={styles.presetRow} role="group" aria-label="فترات جاهزة">
            {RANGE_PRESETS.map((preset) => {
              const active =
                range.from === preset.from() && range.to === preset.to();
              return (
                <button
                  key={preset.key}
                  type="button"
                  className={`${styles.presetButton} ${
                    active ? styles.presetButtonActive : ''
                  }`}
                  aria-pressed={active}
                  onClick={() => applyPreset(preset)}
                >
                  {preset.label}
                </button>
              );
            })}
            {/* "Since launch" is the cleared state, so it reads as one more
                choice in the same group rather than a separate reset. */}
            <button
              type="button"
              className={`${styles.presetButton} ${
                !hasRange ? styles.presetButtonActive : ''
              }`}
              aria-pressed={!hasRange}
              onClick={clearRange}
            >
              منذ البداية
            </button>
          </div>

          <div className={styles.rangeRow}>
            <input
              type="date"
              aria-label="من تاريخ"
              className={`${styles.input} ${rangeError ? styles.inputInvalid : ''}`}
              value={draft.from}
              max={draft.to || undefined}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
            <span className={styles.rangeDash} aria-hidden="true">
              —
            </span>
            <input
              type="date"
              aria-label="إلى تاريخ"
              className={`${styles.input} ${rangeError ? styles.inputInvalid : ''}`}
              value={draft.to}
              min={draft.from || undefined}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
            <button
              type="button"
              className={styles.applyButton}
              disabled={Boolean(rangeError) || (!draft.from && !draft.to)}
              onClick={() => setRange(draft)}
            >
              تطبيق
            </button>
            {/* Icon-only, because it repeats an action the presets already
                imply. It keeps a real `aria-label` — an icon button with none
                is announced as "button". */}
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => refetchStats()}
              disabled={isRefetching}
              title="تحديث الأرقام"
              aria-label="تحديث الأرقام"
            >
              <span aria-hidden="true">{isRefetching ? '···' : '⟳'}</span>
            </button>
          </div>
        </div>
      </header>

      {rangeError && (
        <p className={styles.fieldError} role="alert">
          {rangeError}
        </p>
      )}

      {/* ═══════════════════════════════════════════════════════ tiles ═══ */}
      {isLoadingStats ? (
        <div className={styles.card}>
          <Loader />
        </div>
      ) : isStatsError ? (
        <div className={styles.card}>
          <p className={styles.empty}>
            {statsError?.message ?? 'تعذّر تحميل الأرقام. يرجى المحاولة مرة أخرى.'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.tiles}>
            {tiles.map((tile) => (
              <div className={styles.tile} key={tile.key}>
                <span className={styles.tileValue}>{formatNumber(tile.value)}</span>
                <span className={styles.tileLabel}>{tile.label}</span>
              </div>
            ))}
          </div>

          {/* ═══════════════════════════════════════════════════ chart ═══ */}
          <section className={styles.card}>
            <div className={styles.cardHead}>
              <h3 className={styles.cardTitle}>الاتجاه اليومي</h3>
              {/* Which window the chart is showing, stated where the chart is.
                  The tiles above are fixed windows by definition («اليوم»,
                  «آخر 7 أيام»), so the range deliberately does not touch them —
                  it would make their own labels untrue. */}
              <span className={styles.cardMeta}>
                {hasRange
                  ? 'الفترة المحددة · المربعات أعلاه ثابتة ولا تتأثر بها'
                  : 'كامل المدة المسجّلة'}
              </span>
            </div>

            {rangeSummary && (
              <div className={styles.summaryStrip} role="status">
                <span>
                  <strong>{formatNumber(rangeSummary.visits)}</strong> زيارة
                </span>
                <span className={styles.summaryDivider} aria-hidden="true" />
                <span>
                  <strong>{rangeSummary.days}</strong> يومًا
                </span>
                <span className={styles.summaryDivider} aria-hidden="true" />
                <span>
                  معدل يومي <strong>{formatNumber(rangeSummary.average)}</strong>
                </span>
                {rangeSummary.best && (
                  <>
                    <span className={styles.summaryDivider} aria-hidden="true" />
                    <span>
                      أعلى يوم <strong>{formatDay(rangeSummary.best.date)}</strong> (
                      {formatNumber(rangeSummary.best.visits)})
                    </span>
                  </>
                )}
                {/* Compact, but never dropped: summing the daily unique figures
                    is the obvious move and the wrong one. */}
                <span className={styles.summaryNote}>
                  لا يُجمع عدد الفريدين للفترة — الزائر نفسه قد يتكرر في أكثر من
                  يوم.
                </span>
              </div>
            )}

            {(stats?.daily ?? []).length > 0 ? (
              <div className={styles.chart}>
                <ReactApexChart
                  options={chart.options}
                  series={chart.series}
                  type="area"
                  height={300}
                />
              </div>
            ) : (
              <p className={styles.empty}>
                {hasRange
                  ? 'لا توجد زيارات مسجّلة في هذه الفترة.'
                  : 'لا توجد بيانات يومية بعد.'}
              </p>
            )}
          </section>
        </>
      )}

      {/* ═══════════════════════════════════════════ footer setting ═══════
          A SETTING, not data — so it sits last, and on one line. It used to
          open the page inside a full card, which put a switch nobody touches
          twice a year above the numbers people come here to read. */}
      <section className={styles.settingBar}>
        {isLoadingSettings ? (
          <Loader />
        ) : isSettingsError ? (
          <p className={styles.empty}>تعذّر تحميل الإعداد. يرجى المحاولة مرة أخرى.</p>
        ) : (
          <>
            <div className={styles.settingMain}>
              {isSaving ? (
                <LoaderButton />
              ) : (
                <label className={styles.checkField}>
                  <input
                    type="checkbox"
                    className={styles.switch}
                    checked={isEnabled}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (!next) {
                        setPendingDisable(true);
                        return;
                      }
                      applyToggle(true);
                    }}
                  />
                  <span className={styles.checkText}>
                    <span className={styles.checkTitle}>
                      إظهار عدد الزوار في تذييل الموقع
                      <span
                        className={isEnabled ? styles.pillOn : styles.pillOff}
                      >
                        {isEnabled ? 'ظاهر' : 'مخفي'}
                      </span>
                    </span>
                    {/* The single most misread thing on this screen, kept beside
                        the switch instead of in a box of its own. */}
                    <span className={styles.fieldHint}>
                      الإخفاء لا يوقف الاحتساب — تستمر الزيارات في التسجيل ويبقى
                      الرقم هنا. قد يستغرق التغيير دقيقة ليظهر على الموقع.
                    </span>
                  </span>
                </label>
              )}
            </div>

            {settings?.updatedOn && (
              <p className={styles.settingMeta}>
                آخر تعديل {formatInstant(settings.updatedOn)}
                {settings.updatedBy ? ` — ${settings.updatedBy}` : ''}
              </p>
            )}
          </>
        )}
      </section>

      {pendingDisable && (
        <div className={styles.confirm} role="alertdialog" aria-live="polite">
          <p className={styles.confirmText}>
            سيختفي عدد الزوار من تذييل الموقع لجميع الزوار. هل تريد المتابعة؟
          </p>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={() => applyToggle(false)}
            >
              إخفاء العدد
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              onClick={() => setPendingDisable(false)}
            >
              إلغاء
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitorCounterPage;
